import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import AdminHeader from '../../components/headers/AdminHeader';
import iconTitle from '../../assets/img/icon-square.svg';
import { fetchAllNotices, registerNotice, updateNotice } from '../../services/api/NoticeService';
import { showSuccessDialog } from '../../providers/ErrorDialogProvider';
import '../../styles/NoticeForm.css';
import '../../styles/Tabs.css';
import { useErrorDialog } from '../../providers/ErrorDialogProvider';

const emptyForm = {
  id: null,
  title: '',
  content: '',
  published_from: '',
  published_to: '',
  notice_from: '',
  notice_to: ''
};

const NoticeForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();

  const isEdit = useMemo(() => Boolean(id), [id]);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);

  const showError = useErrorDialog().showError;

  useEffect(() => {
    const initEditData = async () => {
      if (!isEdit) return;

      const stateNotice = location.state?.notice;
      if (stateNotice && String(stateNotice.id) === String(id)) {
        setForm({
          id: stateNotice.id,
          title: stateNotice.title || '',
          content: stateNotice.content || '',
          published_from: stateNotice.published_from,
          published_to: stateNotice.published_to,
          notice_from: stateNotice.notice_from,
          notice_to: stateNotice.notice_to
        });
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const all = await fetchAllNotices();
        const target = (Array.isArray(all) ? all : []).find((item) => String(item.id) === String(id));

        if (!target) {
          navigate('/admin/notices');
          return;
        }

        setForm({
          id: target.id,
          title: target.title || '',
          content: target.content || '',
          published_from: target.published_from,
          published_to: target.published_to,
          notice_from: target.notice_from,
          notice_to: target.notice_to
        });
      } catch (error) {
        navigate('/admin/notices');
      } finally {
        setLoading(false);
      }
    };

    initEditData();
  }, [id, isEdit, location.state, navigate]);

  const onChange = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value
    }));
  };

  const validate = () => {
    const errors = {};

    if (!form.title.trim()) errors.title = 'タイトルは必須です。';
    if (!form.content.trim()) errors.content = '内容は必須です。';
    if (!form.published_from) errors.published_from = '公開開始日時は必須です。';
    if (!form.published_to) errors.published_to = '公開終了日時は必須です。';
    if (!form.notice_from) errors.notice_from = '通知開始日時は必須です。';
    if (!form.notice_to) errors.notice_to = '通知終了日時は必須です。';

    const publishedFrom = form.published_from ? new Date(form.published_from) : null;
    const publishedTo = form.published_to ? new Date(form.published_to) : null;

    if (publishedFrom && publishedTo && publishedFrom > publishedTo) {
      errors.published_to = '公開終了日時は公開開始日時以降を指定してください。';
    }

    const noticeFrom = form.notice_from ? new Date(form.notice_from) : null;
    const noticeTo = form.notice_to ? new Date(form.notice_to) : null;
    if (noticeFrom && noticeTo && noticeFrom > noticeTo) {
      errors.notice_to = '通知終了日時は通知開始日時以降を指定してください。';
    }

    return errors
  };

  const onSubmit = async () => {
    const errors = validate();
    if (Object.keys(errors).length > 0) {
      showError({
        message: Object.values(errors).join('\n'),
        type: 'VALIDATION_ERROR',
        statusCode: 422,
        error: {
          type: 'VALIDATION_ERROR',
          code: 'AGENCY_VALIDATION_FAILED'
        }
      });
      return;
    }

    const payload = {
      id: isEdit ? Number(id) : null,
      title: form.title.trim(),
      content: form.content.trim(),
      published_from: form.published_from,
      published_to: form.published_to,
      notice_from: form.notice_from,
      notice_to: form.notice_to
    };

    try {
      setSubmitting(true);
      if (isEdit) {
        await updateNotice(payload);
      } else {
        await registerNotice(payload);
      }
      showSuccessDialog(isEdit ? 'おしらせを更新しました。' : 'おしらせを登録しました。');
      navigate('/admin/notices');
    } catch (error) {
      // エラーダイアログ表示はBaseApiServiceで行う
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="header-container-admin">
      <AdminHeader onMenuToggle={() => { }} />
      <div className="page-container">
        <div className="content-wrapper">
          <div className="notice-form-container">
            <div className="page-header">
              <div className="title-section">
                <div className="icon-square">
                  <img src={iconTitle} alt="icon" />
                </div>
                <h1>{isEdit ? 'おしらせ詳細（編集）' : 'おしらせ新規登録'}</h1>
              </div>
            </div>

            {loading ? (
              <div className="loading">読み込み中...</div>
            ) : (
              <div className="notice-form-grid">
                <table className='info-table c-max-middle'>
                  <tbody>
                    <tr>
                      <th>タイトル<span className="required-mark required-mark-red">*</span></th>
                      <td colSpan="3">
                        <input
                          className="form-input"
                          type="text"
                          name="title"
                          value={form.title}
                          onChange={(e) => onChange('title', e.target.value)}
                          maxLength={255}
                          required
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>内容<span className="required-mark required-mark-red">*</span></th>
                      <td colSpan="3">
                        <textarea
                          className="notice-form-textarea"
                          value={form.content}
                          onChange={(e) => onChange('content', e.target.value)}
                          rows={8}
                          maxLength={400}
                          required
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>公開開始日時<span className="required-mark required-mark-red">*</span></th>
                      <td>
                        <input
                          type="datetime-local"
                          className="notice-form-input"
                          value={form.published_from}
                          onChange={(e) => onChange('published_from', e.target.value)}
                          required
                        />
                      </td>
                      <th>公開終了日時<span className="required-mark required-mark-red">*</span></th>
                      <td>
                        <input
                          type="datetime-local"
                          className="notice-form-input"
                          value={form.published_to}
                          onChange={(e) => onChange('published_to', e.target.value)}
                          required
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>通知開始日時<span className="required-mark required-mark-red">*</span></th>
                      <td>
                        <input
                          type="datetime-local"
                          className="notice-form-input"
                          value={form.notice_from}
                          onChange={(e) => onChange('notice_from', e.target.value)}
                          required
                        />
                      </td>
                      <th>通知終了日時<span className="required-mark required-mark-red">*</span></th>
                      <td>
                        <input
                          type="datetime-local"
                          className="notice-form-input"
                          value={form.notice_to}
                          onChange={(e) => onChange('notice_to', e.target.value)}
                          required
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="button-group button-group--mt-l c-justify-center">
            <button type="button" className="button button-secondary button-medium" onClick={() => navigate('/admin/notices')}>
              戻る
            </button>
            <button type="button" className="button button-primary button-medium" disabled={submitting || loading} onClick={onSubmit}>
              {isEdit ? '変更を保存する' : '登録する'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NoticeForm;